import {EAIClient} from "Services/EAIClient";
import {flow, makeObservable, observable} from "mobx";
import {appStore} from "Stores/index";

interface DefaultResult {
    totalCount: number
}

export interface FeatureGating {
    school_id: string
    component: string
    name: string
    enabled: boolean
    description: string
    feature_ruuid: string
    settings: unknown
}

export interface Feature {
    ruuid: string
    component: string
    name: string
    description: string
    enabled: boolean
    all_schools: boolean
    settings: unknown
}

export interface SchoolFeature {
    ruuid: string
    school_id: string
    feature_id: string
    enabled: boolean
    settings: unknown
    component: string
    name: string
}

export interface FeatureGatingDataQuery {
    data: FeatureGating[]
    result: DefaultResult
}

export interface FeaturesDataQuery {
    data: Feature[]
    result: DefaultResult
}

export interface SchoolFeaturesDataQuery {
    data: SchoolFeature[]
    result: DefaultResult
}

export interface FeaturesTableParams {
    pageSize: number,
    page: number,
}

class FeatureGatingStore {
    featureGatingList: FeatureGating[] = [];
    features: Feature[] = [];
    schoolFeatures: SchoolFeature[] = [];
    totalFeatureGatingList = 0;
    totalFeatures = 0;
    totalSchoolFeatures = 0;
    isFetchingFeatureGatingList = false;
    isFetchingFeatures = false;
    isFetchingSchoolFeatures = false;

    client = new EAIClient();

    constructor() {
        makeObservable(this, {
            features: observable,
            featureGatingList: observable,
            schoolFeatures: observable,
        });
    }

    fetchFeatureGatingList = flow(function* (this: FeatureGatingStore, schoolId?: string, reset=false) {
        this.isFetchingFeatureGatingList = true;
        try {
            const featuresResp = yield this.client.fetchFeatureGatingList(schoolId, reset);
            this.featureGatingList = featuresResp.data.data as FeatureGating[];
            this.totalFeatureGatingList = this.featureGatingList.length;
            return this.featureGatingList;
        } catch (error) {
            appStore.addError(error);
        } finally {
            this.isFetchingFeatureGatingList = false;
        }
    });

    fetchFeatures = flow(function* (this: FeatureGatingStore, tableParams: FeaturesTableParams) {
        this.isFetchingFeatures = true;
        try {
            const featuresResp = yield this.client.fetchFeatures(tableParams);
            this.features = featuresResp.data.data as Feature[];
            this.totalFeatures = featuresResp.data.result.total_count;
            return this.features;
        } catch (error) {
            appStore.addError(error);
        } finally {
            this.isFetchingFeatures = false;
        }
    });

    updateFeature = flow(function* (this: FeatureGatingStore, ruuid: string, enabled: boolean, allSchools: boolean, description: string) {
        try {
            yield this.client.updateFeature(ruuid, enabled, allSchools, description);
        } catch (e) {
            appStore.addError(e);
        }
    });
}

export { FeatureGatingStore };
